import { FC, FormEvent } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";

import Button from "@/components/Button";
import { FormLayout } from "@/components/Forms";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import {
  handleFormErrors,
  stringsArrayToDropdownOptions,
} from "@/utils/helpers";
import { categoryOptions, typeOptions } from ".";
import { VideoTipResource } from "@/types/resources";
import FormField from "@/components/Forms/FormField";
import { subcategories } from "@/utils/data/tips";

const subcategoryOptions = stringsArrayToDropdownOptions(subcategories);

const mashupOptions = [
  {
    label: "Yes",
    value: true,
  },
  {
    label: "No",
    value: false,
  },
];

interface FormDataT {
  external_id?: string;
  name?: string;
  thumbnail?: string;
  embed_link?: string;
  duration?: number;
  video_type?: string;
  category?: string;
  subcategory?: string;
  mashup?: boolean;
}

interface Props {
  onClose: () => void;
  editableTip?: VideoTipResource;
}

const TipForm: FC<Props> = ({ onClose, editableTip }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      external_id: editableTip?.external_id,
      name: editableTip?.name,
      thumbnail: editableTip?.thumbnail ?? "",
      embed_link: editableTip?.embed_link ?? "",
      duration: editableTip?.duration,
      video_type: editableTip?.video_type,
      category: editableTip?.category,
      subcategory: editableTip?.subcategory,
      mashup: editableTip?.mashup,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        await (editableTip
          ? api.put<typeof data, VideoTipResource>(
              `/v1/video-tips/${editableTip.id}`,
              data
            )
          : api.post<typeof data, VideoTipResource>(`/v1/video-tips`, data));

        onClose();

        queryClient.invalidateQueries(["video_tips"], {
          exact: false,
        });

        alerts.success(
          editableTip
            ? "Tip has been successfully updated"
            : "New tip has been successfully added",
          data.name
        );
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel
        onClose={onClose}
        heading={editableTip ? "Edit Tip" : "Create Tip"}
      >
        <SidePanelBody onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                name="external_id"
                placeholder="Add id"
                control={control}
                label="Hack ID"
              />
              <FormField<FormDataT>
                name="name"
                control={control}
                label="Hack Name"
              />
              <FormField<FormDataT>
                name="thumbnail"
                control={control}
                label="Thumbnail"
                placeholder="Add link"
              />
              <FormField<FormDataT>
                name="embed_link"
                control={control}
                label="Embed Link"
                placeholder="Add link"
              />
              <FormField<FormDataT>
                name="duration"
                control={control}
                label="Duration"
                type="duration"
              />
              <FormField<FormDataT, string>
                name="video_type"
                control={control}
                label="Video Type"
                type="select"
                options={typeOptions}
              />
              <FormField<FormDataT, string>
                name="category"
                control={control}
                label="Category"
                type="select"
                options={categoryOptions}
                menuPlacement="top"
              />
              <FormField<FormDataT, string>
                name="subcategory"
                control={control}
                label="Subcategory"
                type="select"
                options={subcategoryOptions}
                menuPlacement="top"
              />
              <FormField<FormDataT, boolean>
                name="mashup"
                control={control}
                label="Mashup"
                type="select"
                options={mashupOptions}
                menuPlacement="top"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              {editableTip ? "Save" : "Add"}
            </Button>
            <Button color="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default TipForm;
